/**
 * A pack link's arrival, drawn as ONE message: the greeting, the site insights, then the offer card.
 *
 * A prepared pack chat stores three agent rows written at two moments. The greeting and the offer card
 * are written when the pack is generated, and the site insights (`metadata.entryInsights`) when the
 * owner clicks the link. Drawn as three rows they carried three toolbars and two times, and the greeting
 * showed the generation time, not the click. Reported from a recording of the Wix link flow on
 * 2026-10-25.
 *
 * So the rows keep their own bodies, and only the last one draws the toolbar, stamped with the insights
 * row's time (the click). The rows stay separate in the store and to the model.
 *
 * Data only, so history, the live push and a reload draw the same message:
 *  - the insights row must sit directly above an offer card, which is the order
 *    `entryInsightsAboveOfferCard` draws;
 *  - the row directly above the insights joins when it is a plain agent row (the greeting).
 *
 * No runtime imports, so a unit test reads it without the app's dependency graph (see CLAUDE.md → Tests).
 */

export interface PackArrivalMessageRow {
  id?: string;
  role?: string;
  createdDate?: string;
  agentId?: string;
  entryInsights?: boolean;
  bandOfferCard?: unknown;
}

export interface PackArrivalMessage {
  /** Every row of the message, in display order. */
  ids: ReadonlySet<string>;
  /** The top row: where the chat opens on arrival. */
  firstId: string;
  /** The offer card row: the only one that draws the toolbar. */
  lastId: string;
  /** The insights row's time, which is when the owner clicked the link. */
  createdDate?: string;
}

function isAgentRow(row: PackArrivalMessageRow): boolean {
  return row.role === 'ASSISTANT' || row.role === 'ASSISTANT_STEP';
}

function isGreetingRow(row: PackArrivalMessageRow | undefined, insights: PackArrivalMessageRow): boolean {
  return !!row?.id && isAgentRow(row) && !row.bandOfferCard && !row.entryInsights && (row.agentId ?? null) === (insights.agentId ?? null);
}

/**
 * The arrival message in rows drawn in display order, or null when none is there. With more than one
 * insights row above a card, the newest one wins.
 *
 * @example
 * packArrivalMessage([greeting, insights, offer]);
 * // => { ids: {greeting, insights, offer}, firstId: greeting, lastId: offer, createdDate: insights.createdDate }
 */
export function packArrivalMessage(rows: ReadonlyArray<PackArrivalMessageRow>): PackArrivalMessage | null {
  for (let index = rows.length - 2; index >= 0; index -= 1) {
    const insights = rows[index];
    const offer = rows[index + 1];
    if (!insights?.id || !insights.entryInsights || !isAgentRow(insights) || !offer?.id || !offer.bandOfferCard) {
      continue;
    }
    const greeting = rows[index - 1];
    const first = greeting && isGreetingRow(greeting, insights) ? greeting : insights;
    const ids = new Set([first.id as string, insights.id, offer.id]);
    return {
      ids,
      firstId: first.id as string,
      lastId: offer.id,
      ...(insights.createdDate ? { createdDate: insights.createdDate } : {}),
    };
  }
  return null;
}
